import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "text-white border border-white/20 hover:bg-white/20 backdrop-blur-sm transition-all duration-300" + " " + "bg-[hsl(210_65%_32%)] hover:bg-[hsl(210_65%_28%)]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border hover:bg-white/20 backdrop-blur-sm transition-all duration-300" + " " + "border-[hsl(210_65%_32%)] text-[hsl(210_65%_32%)] hover:bg-[hsl(210_65%_32%)] hover:text-white",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-hover",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "underline-offset-4 hover:underline" + " " + "text-[hsl(210_65%_32%)]",
        luxury: "bg-gradient-to-r shadow-lg hover:shadow-xl transition-all duration-300 border text-white transform hover:scale-[1.02]" + " " + "from-[hsl(210_65%_32%)] via-[hsl(210_65%_32%)] to-[hsl(210_65%_38%)] border-[hsl(210_65%_32%)]/20",
        hero: "bg-white/10 text-white border border-white/20 hover:bg-white/20 backdrop-blur-sm transition-all duration-300",
        "outline-hero": "border border-primary/20 text-foreground hover:bg-primary hover:text-primary-foreground shadow-minimal hover:shadow-soft transform hover:scale-[1.02] transition-spring",
        premium: "bg-gradient-primary-subtle text-foreground hover:bg-primary hover:text-primary-foreground shadow-soft hover:shadow-luxury transform hover:scale-[1.02] transition-spring",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 px-4",
        lg: "h-14 px-8 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }